import React, { useCallback, useRef, useState } from "react";

import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ShareIcon from "@mui/icons-material/Share";
import { Box, Button, CardMedia, IconButton, Stack, Typography } from "@mui/material";
import { RoleComponentGuard } from "components";
import { AccessDenied } from "components/Access-denied/AccessDenied";
import { ProfilePreviewModal } from "components/modals/Profile-preview-modal/ProfilePreviewModal";
import { AssetAllocation } from "components/Social/Asset-allocation/AssetAllocation";
import { MessagePopper } from "components/Social/Message-popper/MessagePopper";
import { useNavigate, usePaginationHook } from "hooks";
import { useAccountSelector, usePickUserPermissions, useUserActions } from "store";
import { UserPermissionType } from "store/types";

import { RoleName } from "../../constants";

export const Social: React.FC = () => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const { getPaginationProfiles } = useUserActions();
  const navigate = useNavigate();
  const social = usePickUserPermissions("social");
  const isSocialEnabled = useAccountSelector()?.allow_social;
  const carouselRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollState = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  const noAccessPermission = social && social === UserPermissionType.NO_ACCESS;

  const { data, getNext, total } = usePaginationHook(async (page) => getPaginationProfiles(page, 10));

  const selectedProfile = data[selectedIndex];
  const profile = selectedProfile?.details;

  const handleCardClick = (index: number) => {
    setSelectedIndex(index);
  };

  const handleScrollLeft = () => {
    carouselRef.current?.scrollBy({ left: -160, behavior: "smooth" });
  };

  const handleScrollRight = () => {
    carouselRef.current?.scrollBy({ left: 160, behavior: "smooth" });
    if (data.length < total) {
      getNext();
    }
  };

  return (
    data && (
      <RoleComponentGuard
        availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
        hideChild={noAccessPermission}
        RenderInstead={<AccessDenied redirectClick={() => navigate({ route: "main" })} title="Back home" />}
      >
        <Stack
          sx={{
            width: "100%",
            height: "100%",
            bgcolor: "#F6F8FC",
            overflow: "auto",
            alignItems: "center"
          }}
        >
          <Stack sx={{ width: "100%", maxWidth: "1100px", gap: "16px", py: "20px", px: "20px" }}>
            {/* Profile Detail Card */}
            {profile && (
              <Stack alignItems="center">
                <Stack
                  sx={{
                    width: "798px",
                    bgcolor: "#edf1f9",
                    borderRadius: "10px",
                    p: "30px",
                    gap: "33px"
                  }}
                >
                  <Stack direction="row" gap="20px">
                    {profile.profile_photo_url ? (
                      <CardMedia
                        component="img"
                        sx={{
                          width: 203,
                          height: 258,
                          borderRadius: "8px",
                          objectFit: "cover",
                          boxShadow: "2px 2px 2px 0px rgba(0,0,0,0.1)",
                          flexShrink: 0
                        }}
                        src={profile.profile_photo_url}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 203,
                          height: 258,
                          borderRadius: "8px",
                          bgcolor: "#0a3142",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0
                        }}
                      >
                        <AccountCircleIcon sx={{ width: 115, height: 115, fill: "#c09560" }} />
                      </Box>
                    )}

                    <Stack sx={{ flex: 1, gap: "28px" }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Stack direction="row" gap="20px" alignItems="flex-start">
                          <Stack>
                            <Typography sx={{ fontSize: "34px", color: "#0a3142", fontWeight: 700, lineHeight: 1.2 }}>
                              {profile.first_name}
                            </Typography>
                            <Typography sx={{ fontSize: "34px", color: "#0a3142", fontWeight: 700, lineHeight: 1.2 }}>
                              {profile.last_name}
                            </Typography>
                          </Stack>

                          <Box sx={{ width: "1px", alignSelf: "stretch", bgcolor: "#5d7782", borderRadius: "8px" }} />

                          <Stack gap="8px">
                            {profile.position && (
                              <Stack>
                                <Typography sx={{ fontSize: "14px", color: "#5d7782" }}>Position</Typography>
                                <Typography sx={{ fontSize: "14px", color: "#0a3142" }}>{profile.position}</Typography>
                              </Stack>
                            )}
                            {profile.company_name && (
                              <Stack>
                                <Typography sx={{ fontSize: "14px", color: "#5d7782" }}>Company</Typography>
                                <Typography sx={{ fontSize: "14px", color: "#0a3142" }}>
                                  {profile.company_name}
                                </Typography>
                              </Stack>
                            )}
                            {profile.location && (
                              <Stack>
                                <Typography sx={{ fontSize: "14px", color: "#5d7782" }}>Location</Typography>
                                <Typography sx={{ fontSize: "14px", color: "#0a3142" }}>{profile.location}</Typography>
                              </Stack>
                            )}
                          </Stack>
                        </Stack>

                        <Stack gap="10px" alignItems="flex-end">
                          <MessagePopper
                            title={`Connect to ${profile.full_name}`}
                            subTitle="Introduce yourself"
                            btnTitle=""
                            userId={profile.id}
                            type="connect"
                          >
                            <Button
                              disabled={!isSocialEnabled}
                              variant="contained"
                              startIcon={<ShareIcon sx={{ fontSize: "16px !important" }} />}
                              sx={{
                                bgcolor: "#26a6ae",
                                borderRadius: "32px",
                                textTransform: "none",
                                fontSize: "12px",
                                lineHeight: "16px",
                                px: "8px",
                                py: "4px",
                                minWidth: "100px",
                                "&:hover": { bgcolor: "#1e8f96" }
                              }}
                            >
                              Connect
                            </Button>
                          </MessagePopper>
                          <Button
                            variant="outlined"
                            startIcon={<PersonOutlineIcon sx={{ fontSize: "16px !important" }} />}
                            onClick={() => setProfileModalOpen(true)}
                            sx={{
                              borderRadius: "32px",
                              textTransform: "none",
                              fontSize: "12px",
                              lineHeight: "16px",
                              px: "8px",
                              py: "4px",
                              color: "#071f35",
                              borderColor: "#7b92a9",
                              "&:hover": { borderColor: "#5d7782", bgcolor: "transparent" }
                            }}
                          >
                            View Profile
                          </Button>
                        </Stack>
                      </Stack>

                      {selectedProfile?.asset_allocations?.length > 0 && (
                        <Stack key={profile.id} gap="12px">
                          <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#0a3142" }}>
                            Asset Allocation
                          </Typography>
                          <AssetAllocation data={selectedProfile.asset_allocations} />
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            )}

            {/* Contact Cards Carousel */}
            <Stack direction="row" alignItems="center" justifyContent="center">
              <Stack sx={{ flex: 1, alignItems: "flex-end", justifyContent: "center", py: "10px" }}>
                <IconButton onClick={handleScrollLeft} disabled={!canScrollLeft} sx={{ width: 48, height: 48 }}>
                  <ArrowBackIosNewIcon sx={{ fontSize: 40, color: canScrollLeft ? "#0a3142" : "#bac7ce" }} />
                </IconButton>
              </Stack>

              <Stack
                ref={carouselRef}
                direction="row"
                onScroll={updateScrollState}
                sx={{
                  width: "798px",
                  gap: "10px",
                  overflowX: "auto",
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": { display: "none" }
                }}
              >
                {data.map((profileData, index) => {
                  const details = profileData.details;
                  const isSelected = index === selectedIndex;

                  return (
                    <Stack
                      key={details.id}
                      onClick={() => handleCardClick(index)}
                      sx={{
                        minWidth: "150px",
                        width: "150px",
                        bgcolor: "white",
                        borderRadius: "8px",
                        p: "16px",
                        gap: "10px",
                        cursor: "pointer",
                        boxShadow: "0px 1px 1px rgba(18,18,23,0.1)",
                        border: isSelected ? "1px solid #071f35" : "1px solid transparent",
                        "&:hover": { border: "1px solid #071f35" }
                      }}
                    >
                      {details.profile_photo_url ? (
                        <CardMedia
                          component="img"
                          sx={{ width: "100%", height: 135, borderRadius: "4px", objectFit: "cover" }}
                          src={details.profile_photo_url}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: 135,
                            borderRadius: "4px",
                            bgcolor: "#804099",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                        >
                          <AccountCircleIcon sx={{ width: 60, height: 60, fill: "rgba(255,255,255,0.5)" }} />
                        </Box>
                      )}

                      <Stack gap="2px">
                        <Typography
                          noWrap
                          sx={{ fontSize: "16px", fontWeight: 700, color: "#071f35", lineHeight: "24px" }}
                        >
                          {details.full_name}
                        </Typography>
                        <Typography sx={{ fontSize: "14px", color: "#071f35", lineHeight: "16px" }}>
                          {details.position}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: "12px", color: "#496682", lineHeight: "16px" }}>
                          {details.company_name}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: "12px", color: "#496682", lineHeight: "16px" }}>
                          {details.location}
                        </Typography>
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>

              <Stack sx={{ flex: 1, alignItems: "flex-start", justifyContent: "center", py: "10px" }}>
                <IconButton onClick={handleScrollRight} disabled={!canScrollRight} sx={{ width: 48, height: 48 }}>
                  <ArrowForwardIosIcon sx={{ fontSize: 40, color: canScrollRight ? "#0a3142" : "#bac7ce" }} />
                </IconButton>
              </Stack>
            </Stack>
          </Stack>
        </Stack>

        <ProfilePreviewModal
          profile={profileModalOpen ? selectedProfile : null}
          onClose={() => setProfileModalOpen(false)}
        />
      </RoleComponentGuard>
    )
  );
};
